import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { Transaction } from "@mysten/sui/transactions";
import { useCurrentAccount, useSignAndExecuteTransaction } from "@mysten/dapp-kit";
import { SuiClient } from "@mysten/sui/client";
import { isMobileDevice } from "./utils/mobileWallet";
import logger from './utils/logger';
import {
  RiWallet3Line,
  RiUser3Line,
  RiShieldCheckLine,
  RiEditLine,
  RiFileCopyLine,
  RiEyeOffLine,
  RiVipCrownLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiSettings3Line,
} from 'react-icons/ri';
import './Settings.css';

// ─── Wallet Address Display ───────────────────────────────────────────────────
function WalletAddressDisplay({ address }) {
  const [isVisible, setIsVisible] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const holdTimeoutRef = useRef(null);

  const startHide = () => {
    holdTimeoutRef.current = setTimeout(() => setIsVisible(false), 1000);
  };
  const cancelHide = () => {
    if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      logger.error('Failed to copy:', err);
    }
  };

  if (!isVisible) {
    return (
      <button className="wallet-hidden-btn" onClick={() => setIsVisible(true)}>
        <RiEyeOffLine size={14} />
        Address hidden — tap to reveal
      </button>
    );
  }

  return (
    <>
      <div
        className="wallet-address-box"
        onClick={copyToClipboard}
        onMouseDown={startHide}
        onMouseUp={cancelHide}
        onMouseLeave={cancelHide}
        onTouchStart={startHide}
        onTouchEnd={cancelHide}
        title="Click to copy · Hold 1 second to hide"
        style={{ filter: isCopied ? 'blur(2px)' : 'none', transition: 'filter 0.3s' }}
      >
        {address}
        {isCopied && (
          <span style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#00ff88', fontWeight: 700, fontSize: 14,
            textShadow: '0 0 8px #00ff88', background: 'rgba(0,0,0,0.5)',
            borderRadius: 10,
          }}>
            <RiCheckLine /> Copied!
          </span>
        )}
      </div>
      <div className="wallet-hint">
        <RiFileCopyLine size={11} /> Click to copy &nbsp;·&nbsp;
        <RiEyeOffLine size={11} /> Hold 1s to hide
      </div>
    </>
  );
}

// ─── Settings Component ───────────────────────────────────────────────────────
function Settings() {
  const currentAccount = useCurrentAccount();
  const isConnected = !!currentAccount;
  const { mutate: signAndExecuteTransactionBlock } = useSignAndExecuteTransaction();

  // State
  const [displayName, setDisplayName]           = useState("");
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [error, setError]                        = useState(null);
  const [nameChangeCount, setNameChangeCount]    = useState(0);
  const [remainingFreeChanges, setRemainingFreeChanges] = useState(3);
  const [encryptMessages, setEncryptMessages]    = useState(false);
  const [disableWalletPopup, setDisableWalletPopup] = useState(
    localStorage.getItem("disableWalletPopup") === "true"
  );
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [donationAmount, setDonationAmount]      = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isRegistered, setIsRegistered]          = useState(false);
  const [refreshTrigger, setRefreshTrigger]      = useState(0);

  // Fetch current profile
  useEffect(() => {
    const fetchCurrentName = async () => {
      if (!isConnected || !currentAccount) return;
      try {
        const client = new SuiClient({ url: "https://fullnode.mainnet.sui.io:443" });
        const objects = await client.getOwnedObjects({
          owner: currentAccount.address,
          options: { showType: true, showContent: true },
        });
        const userObject = objects.data.find((obj) =>
          obj.data.type.includes(
            "0x2aaad5d1ce7482b5850dd11642358bf23cb0e6432b12a581eb77d212dca54045::su_messaging::User"
          )
        );
        if (userObject) {
          setIsRegistered(true);
          const savedName = userObject.data.content.fields.display_name
            ? new TextDecoder().decode(new Uint8Array(userObject.data.content.fields.display_name))
            : localStorage.getItem("currentDisplayName");
          const changeCount = userObject.data.content.fields.name_change_count || 0;
          setNameChangeCount(changeCount);
          setRemainingFreeChanges(Math.max(0, 3 - changeCount));
          if (savedName) setDisplayName(savedName);
        } else {
          setIsRegistered(false);
        }
      } catch (e) { logger.error(e); }
    };
    fetchCurrentName();
  }, [isConnected, currentAccount, refreshTrigger]);

  const refreshRegistrationStatus = () => setRefreshTrigger(prev => prev + 1);

  // Register
  const handleRegister = async () => {
    if (!isConnected) { setError("Please connect your wallet first."); return; }
    setError(null);
    setRegistrationStatus("Registering...");
    try {
      const tx = new Transaction();
      const packageId = "0x2aaad5d1ce7482b5850dd11642358bf23cb0e6432b12a581eb77d212dca54045";
      tx.moveCall({ target: `${packageId}::su_messaging::register`, arguments: [] });
      signAndExecuteTransactionBlock(
        { transaction: tx, options: isMobileDevice() ? { showEffects: true, showEvents: true } : { showEffects: true, showEvents: true, showObjectChanges: true } },
        {
          onSuccess: () => {
            setRegistrationStatus("Registration successful! You can now set your display name.");
            setTimeout(() => refreshRegistrationStatus(), 2000);
          },
          onError: (err) => {
            setError("Registration failed: " + err.message);
            setRegistrationStatus(null);
          },
        }
      );
    } catch (err) {
      setError("Registration failed: " + err.message);
      setRegistrationStatus(null);
    }
  };

  // Update display name
  const handleRegisterOrUpdate = async (e) => {
    e.preventDefault();
    if (!isConnected) { setError("Please connect your wallet first."); return; }
    setError(null);
    setRegistrationStatus(null);
    try {
      const tx = new Transaction();
      const packageId = "0x2aaad5d1ce7482b5850dd11642358bf23cb0e6432b12a581eb77d212dca54045";
      const client = new SuiClient({ url: "https://fullnode.mainnet.sui.io:443" });
      const objects = await client.getOwnedObjects({
        owner: currentAccount.address,
        options: { showType: true, showContent: true },
      });
      const userObject = objects.data.find((obj) =>
        obj.data.type.includes(
          "0x2aaad5d1ce7482b5850dd11642358bf23cb0e6432b12a581eb77d212dca54045::su_messaging::User"
        )
      );
      if (!userObject) { setError("User profile not found. Please register first."); return; }
      const userObjectId = userObject.data.objectId;

      if (nameChangeCount >= 3) {
        const [paymentCoin] = tx.splitCoins(tx.gas, [Math.floor(0.001 * 1e9)]);
        tx.moveCall({
          target: `${packageId}::su_messaging::update_name_paid`,
          arguments: [tx.object(userObjectId), tx.pure("vector<u8>", new TextEncoder().encode(displayName)), paymentCoin],
        });
      } else {
        tx.moveCall({
          target: `${packageId}::su_messaging::update_name_free`,
          arguments: [tx.object(userObjectId), tx.pure("vector<u8>", new TextEncoder().encode(displayName))],
        });
      }

      await signAndExecuteTransactionBlock({
        transaction: tx,
        options: isMobileDevice() ? { showEffects: true } : (disableWalletPopup ? { showEffects: true } : { showEffects: true, showObjectChanges: true }),
      });

      localStorage.setItem("currentDisplayName", displayName);
      setNameChangeCount(nameChangeCount + 1);
      setRemainingFreeChanges(Math.max(0, 3 - (nameChangeCount + 1)));
      setRegistrationStatus(
        nameChangeCount >= 3
          ? "Display name updated! (Paid change)"
          : `Display name updated! (${2 - nameChangeCount} free change${2 - nameChangeCount !== 1 ? 's' : ''} remaining)`
      );
    } catch (err) {
      setError("Name update failed: " + err.message);
    }
  };

  const handleToggleWalletPopup = () => {
    const newValue = !disableWalletPopup;
    setDisableWalletPopup(newValue);
    localStorage.setItem("disableWalletPopup", newValue);
  };

  const handleSubscribe = async () => {
    if (!isConnected) { setSubscriptionStatus("Please connect your wallet first."); return; }
    setError(null);
    setSubscriptionStatus(null);
    try {
      const tx = new Transaction();
      const donation = parseFloat(donationAmount) || 0;
      tx.transferObjects(
        tx.splitCoins(tx.gas, [Math.floor(0.5 * 1e9)]),
        "0xd1b0ff621a6803c8f0cd8051359ce312ece62b485e010e32b58a99d5ec13201c"
      );
      if (donation > 0) {
        tx.transferObjects(
          tx.splitCoins(tx.gas, [Math.floor(donation * 1e9)]),
          "0xd1b0ff621a6803c8f0cd8051359ce312ece62b485e010e32b58a99d5ec13201c"
        );
      }
      await signAndExecuteTransactionBlock({
        transaction: tx,
        options: isMobileDevice() ? { showEffects: true } : { showEffects: true, showObjectChanges: true },
      });
      setSubscriptionStatus("Subscription successful! Membership granted.");
    } catch (err) {
      setSubscriptionStatus("Subscription failed: " + err.message);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="settings-page">
      <div className="settings-content">

        {/* ── Page Header ── */}
        <div className="settings-page-header">
          <h1>
            <RiSettings3Line size={24} />
            Settings
          </h1>
          <button className="settings-subscribe-btn" onClick={() => setShowSubscriptionModal(true)}>
            🦅 Subscribe
          </button>
        </div>

        {/* ── Status Messages ── */}
        {registrationStatus && (
          <div className="settings-alert settings-alert-success">
            <RiCheckLine size={16} /> {registrationStatus}
          </div>
        )}
        {error && (
          <div className="settings-alert settings-alert-error">
            <RiErrorWarningLine size={16} /> {error}
          </div>
        )}

        {/* ── Wallet Card ── */}
        {isConnected && currentAccount && (
          <div className="settings-card">
            <div className="settings-card-header">
              <RiWallet3Line size={14} /> Connected Wallet
            </div>
            <div className="settings-card-body">
              <div className="wallet-label">Your Address</div>
              <WalletAddressDisplay address={currentAccount.address} />
            </div>
          </div>
        )}

        {/* ── Registration Card (when not registered) ── */}
        {!isRegistered && (
          <div className="settings-card">
            <div className="settings-card-header">
              <RiUser3Line size={14} /> Blockchain Profile
            </div>
            <div className="settings-card-body">
              <div className="register-hero">
                <p>
                  Register on the Sui blockchain to set a display name and unlock all messaging features.
                  Registration is a one-time transaction.
                </p>
                <button
                  className="settings-btn settings-btn-primary"
                  onClick={handleRegister}
                  disabled={registrationStatus === "Registering..."}
                >
                  {registrationStatus === "Registering..." ? "Registering…" : "Register Now"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Display Name Card (when registered) ── */}
        {isRegistered && (
          <div className="settings-card">
            <div className="settings-card-header">
              <RiUser3Line size={14} /> Display Name
              {remainingFreeChanges > 0 ? (
                <span className="name-changes-badge badge-free">
                  {remainingFreeChanges} free {remainingFreeChanges === 1 ? 'change' : 'changes'} left
                </span>
              ) : (
                <span className="name-changes-badge badge-paid">
                  0.001 SUI per change
                </span>
              )}
            </div>
            <div className="settings-card-body">
              <form onSubmit={handleRegisterOrUpdate}>
                <label className="settings-field-label">
                  Display Name on SUI Blockchain
                </label>
                <input
                  className="settings-input"
                  type="text"
                  placeholder="Enter your display name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  style={{ marginBottom: '16px' }}
                />
                <button type="submit" className="settings-btn settings-btn-primary">
                  <RiEditLine size={14} /> Update Name
                </button>
              </form>
            </div>
          </div>
        )}

        {/* ── Preferences Card ── */}
        <div className="settings-card">
          <div className="settings-card-header">
            <RiShieldCheckLine size={14} /> Preferences
          </div>
          <div className="settings-card-body">
            <div className="settings-row">
              <div className="settings-row-info">
                <div className="settings-row-title">Message Encryption</div>
                <div className="settings-row-desc">End-to-end encrypt your messages using AES-256-GCM</div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={encryptMessages}
                  onChange={(e) => setEncryptMessages(e.target.checked)}
                />
                <span className="settings-toggle-slider" />
              </label>
            </div>
            <div className="settings-row">
              <div className="settings-row-info">
                <div className="settings-row-title">Disable Wallet Pop-up</div>
                <div className="settings-row-desc">Skip confirmation dialogs — free option for faster messaging</div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={disableWalletPopup}
                  onChange={handleToggleWalletPopup}
                />
                <span className="settings-toggle-slider" />
              </label>
            </div>
          </div>
        </div>

        {/* ── About Card ── */}
        <div className="settings-card">
          <div className="settings-card-header">
            <RiVipCrownLine size={14} /> About SU Messaging
          </div>
          <div className="settings-card-body">
            <div className="settings-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
              <div className="settings-row-title">Decentralized Messaging</div>
              <div className="settings-row-desc">Powered by the Sui blockchain with Walrus and IPFS storage. Your messages are encrypted and stored securely on decentralized infrastructure.</div>
            </div>
            <div className="settings-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
              <div className="settings-row-title">Membership</div>
              <div className="settings-row-desc">Support the project with a 0.5 SUI subscription (≈ $10) for premium membership access.</div>
            </div>
            <div style={{ marginTop: 16 }}>
              <button
                className="settings-btn settings-btn-outline"
                onClick={() => setShowSubscriptionModal(true)}
              >
                🦅 View Membership Plans
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* ── Subscription Modal ── */}
      <Modal
        show={showSubscriptionModal}
        onHide={() => setShowSubscriptionModal(false)}
        centered
        contentClassName="settings-modal-content"
      >
        <Modal.Header
          closeButton
          className="sub-modal-header"
          style={{ background: '#0d001f', borderBottom: '1px solid rgba(255,0,255,0.3)', color: '#00ffff' }}
        >
          <Modal.Title>
            <span style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 16, color: '#00ffff', textShadow: '0 0 10px rgba(0,255,255,0.5)' }}>
              🦅 Membership Subscription
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#0a001a', padding: '24px', color: '#fff' }}>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(255,255,255,0.85)', marginBottom: 8, fontFamily: 'system-ui, sans-serif' }}>
            Become a member by sending <strong style={{ color: '#ffd700' }}>0.5 SUI (≈ $10)</strong> to:
          </p>
          <div className="sub-address">
            0xd1b0ff621a6803c8f0cd8051359ce312ece62b485e010e32b58a99d5ec13201c
          </div>
          <p className="sub-amount-note">Minimum amount required is 0.5 SUI.</p>

          <div className="sub-buttons">
            <button className="sub-btn-pay" onClick={handleSubscribe}>
              Pay Subscription (0.5 SUI)
            </button>
          </div>

          <span className="sub-input-label">Optional Donation Amount (SUI)</span>
          <input
            className="sub-input"
            type="number"
            placeholder="0.00"
            min="0"
            step="0.001"
            value={donationAmount}
            onChange={(e) => setDonationAmount(e.target.value)}
          />
          <div className="sub-buttons" style={{ marginTop: 12 }}>
            <button
              className="sub-btn-donate"
              onClick={() => { if (donationAmount && parseFloat(donationAmount) > 0) handleSubscribe(); }}
              disabled={!donationAmount || parseFloat(donationAmount) <= 0}
            >
              Donate
            </button>
          </div>

          {subscriptionStatus && (
            <div className={`settings-alert ${subscriptionStatus.includes("successful") ? "settings-alert-success" : "settings-alert-error"}`} style={{ marginTop: 16 }}>
              {subscriptionStatus.includes("successful") ? <RiCheckLine /> : <RiErrorWarningLine />}
              {subscriptionStatus}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default Settings;
